import { useCallback, useContext, useEffect, useState } from 'react'

import { IS_PREBOOK_FLOW, MY_CART } from '@/constants/common'
import { CartContext } from '@/context/CartProvider'
import { ComboVariant2 } from '@/models/Combo'
import { CartStateInStorage, ProductVariant } from '@/models/Product'
import {
  selectCurrentVendorId,
  selectLatLng,
} from '@/store/features/locationSlice'
import fetchComboVariants from '@/utils/api/fetchComboVariants'
import fetchProductVariantsByCatalogueId from '@/utils/api/fetchProductVariantsByCatalogueId'
import { getLocalStorageKey } from '@/utils/helper'
import { syncCartPricingFromCatalog } from '@/utils/helpers/flashSale'

import { useAppSelector } from './reduxHooks'

const useFetchCartProductVariants = () => {
  const { cartItems, comboItems, cartType } = useContext(CartContext)
  const [loading, setLoading] = useState(true)
  const [variants, setVariants] = useState<{
    product: ProductVariant[]
    combo: ComboVariant2[]
  }>({ product: [], combo: [] })
  const latLng = useAppSelector(selectLatLng)
  const vendorId = useAppSelector(selectCurrentVendorId)

  const items = Object.keys({ ...cartItems, ...comboItems })

  const getProductVariants = useCallback(
    async (
      latLng: string,
      catalogueIds: number[],
      vendorId?: string,
      comboIds?: string[],
    ) => {
      setLoading(true)
      try {
        const [comboVariantsResponse, variantsResponse] = await Promise.all([
          comboIds && comboIds.length > 0
            ? fetchComboVariants(
                comboIds,
                latLng,
                vendorId,
                undefined,
                !!getLocalStorageKey(IS_PREBOOK_FLOW),
              )
            : Promise.resolve([]),

          catalogueIds && catalogueIds.length > 0
            ? fetchProductVariantsByCatalogueId(catalogueIds, latLng, vendorId)
            : Promise.resolve([]),
        ])

        setVariants({
          product: syncCartPricingFromCatalog(variantsResponse),
          combo: comboVariantsResponse,
        })
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    const cartData = getLocalStorageKey<CartStateInStorage, null>(MY_CART)
    const catalogueIds = cartData?.[cartType]?.catalogueIds
      ? Object.values(cartData[cartType].catalogueIds)
      : []
    const comboIds = cartData?.[cartType]?.comboItems
      ? Object.keys(cartData[cartType].comboItems)
      : []
    if (
      (catalogueIds.length > 0 || comboIds.length > 0) &&
      latLng &&
      vendorId
    ) {
      getProductVariants(latLng, catalogueIds, vendorId, comboIds)
    } else {
      setLoading(false)
    }
  }, [getProductVariants, cartType, items.length, latLng, vendorId])

  useEffect(() => {
    if (!loading) {
      setVariants((prev) => {
        const nextProduct = prev.product.filter(
          (variant) => !!cartItems[variant.id],
        )

        const nextCombo = prev.combo.filter(
          (variant) => !!comboItems[variant.id],
        )

        const isProductSame =
          nextProduct.length === prev.product.length &&
          nextProduct.every((v, i) => v.id === prev.product[i].id)

        const isComboSame =
          nextCombo.length === prev.combo.length &&
          nextCombo.every((v, i) => v.id === prev.combo[i].id)

        // prevent re-render ONLY if both are unchanged
        if (isProductSame && isComboSame) {
          return prev
        }

        return { product: nextProduct, combo: nextCombo }
      })
    }
    // removed `cartItems` from dep[] as variants only change when length of cartItems change and not qty of individual items.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, loading])

  return { loading, variants }
}

export default useFetchCartProductVariants
